import type { ComponentType } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  CalendarIcon,
  ChartBarIcon,
  ChartTrendingUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CogIcon,
  CoinIcon,
  CurrencyCircleIcon,
  DocumentTextIcon,
  FolderIcon,
  HomeIcon,
  RefreshIcon,
  UserIcon,
  UsersIcon,
} from '../components/ui';
import { hasFeature } from '../components/FeatureRoute';
import { useAuth } from '../hooks/useAuth';
import { t } from '../i18n';

type IconComponent = ComponentType<{ className?: string }>;

type NavItem = {
  to: string;
  label: string;
  icon: IconComponent;
  adminOnly?: boolean;
  feature?: string;
  children?: NavItem[];
};

const OPEN_SECTIONS_KEY = 'bf.sidebar.openSections';

const navItems: NavItem[] = [
  { to: '/', label: t().nav.home, icon: HomeIcon, feature: 'dashboard' },
  {
    to: '/customers',
    label: t().nav.customers,
    icon: UserIcon,
    feature: 'customers',
  },
  {
    to: '/invoices',
    label: t().nav.invoices,
    icon: DocumentTextIcon,
    feature: 'invoices',
  },
  {
    to: '/payments',
    label: t().nav.payments,
    icon: CoinIcon,
    feature: 'payments',
  },
  {
    to: '/company-expenses',
    label: t().nav.companyExpenses,
    icon: CurrencyCircleIcon,
    feature: 'company_expenses',
    children: [
      {
        to: '/company-expenses',
        label: t().nav.companyExpensesList,
        icon: DocumentTextIcon,
        feature: 'company_expenses',
      },
      {
        to: '/company-expenses/payables',
        label: t().nav.companyExpensePayables,
        icon: ClockIcon,
        feature: 'company_expenses',
      },
      {
        to: '/company-expenses/summary',
        label: t().nav.companyExpenseSummary,
        icon: ChartBarIcon,
        feature: 'company_expenses',
      },
      {
        to: '/reports/company-expense-yearly-monthly',
        label: t().nav.companyExpenseYearlyMonthly,
        icon: CalendarIcon,
        feature: 'reports_company_expense_yearly_monthly',
      },
      {
        to: '/reports/company-expense-by-vendor',
        label: t().nav.companyExpenseByVendor,
        icon: UserIcon,
        feature: 'reports_company_expense_by_vendor',
      },
      {
        to: '/reports/company-expense-type-analysis',
        label: t().nav.companyExpenseTypeAnalysis,
        icon: ChartTrendingUpIcon,
        feature: 'reports_company_expense_type_analysis',
      },
      {
        to: '/reports/company-expense-payments',
        label: t().nav.companyExpensePayments,
        icon: CoinIcon,
        feature: 'reports_company_expense_payments',
      },
      {
        to: '/reports/company-expense-aging',
        label: t().nav.companyExpenseAging,
        icon: ClockIcon,
        feature: 'reports_company_expense_aging',
      },
    ],
  },
  {
    to: '/shipment-files',
    label: t().nav.shipmentFiles,
    icon: FolderIcon,
    feature: 'shipment_files',
    children: [
      {
        to: '/shipment-files',
        label: t().nav.shipmentFilesList,
        icon: FolderIcon,
        feature: 'shipment_files',
      },
      {
        to: '/reports/shipment-monthly-summary',
        label: t().nav.shipmentMonthlySummary,
        icon: FolderIcon,
        feature: 'reports_shipment_monthly_summary',
      },
      {
        to: '/reports/shipment-expense-categories',
        label: t().nav.shipmentExpenseCategories,
        icon: ChartTrendingUpIcon,
        feature: 'reports_shipment_expense_categories',
      },
      {
        to: '/reports/shipment-open-files',
        label: t().nav.shipmentOpenFiles,
        icon: FolderIcon,
        feature: 'reports_shipment_open_files',
      },
      {
        to: '/reports/shipment-by-customer',
        label: t().nav.shipmentByCustomer,
        icon: UserIcon,
        feature: 'reports_shipment_by_customer',
      },
      {
        to: '/reports/shipment-container-bl',
        label: t().nav.shipmentContainerBl,
        icon: DocumentTextIcon,
        feature: 'reports_shipment_container_bl',
      },
      {
        to: '/reports/shipment-expense-analysis',
        label: t().nav.shipmentExpenseAnalysis,
        icon: ChartBarIcon,
        feature: 'reports_shipment_expense_analysis',
      },
      {
        to: '/reports/shipment-by-port-vessel',
        label: t().nav.shipmentByPortVessel,
        icon: FolderIcon,
        feature: 'reports_shipment_by_port_vessel',
      },
    ],
  },
  {
    to: '/mydata',
    label: t().nav.mydata,
    icon: DocumentTextIcon,
    feature: 'mydata',
    children: [
      {
        to: '/mydata',
        label: t().nav.mydataPeriod,
        icon: ChartBarIcon,
        feature: 'mydata',
      },
      {
        to: '/mydata/sync',
        label: t().nav.mydataSync,
        icon: RefreshIcon,
        feature: 'mydata',
      },
      {
        to: '/mydata/debts',
        label: t().nav.mydataDebts,
        icon: CurrencyCircleIcon,
        feature: 'mydata',
      },
      {
        to: '/mydata/documents',
        label: t().nav.mydataDocuments,
        icon: DocumentTextIcon,
        feature: 'mydata',
      },
      {
        to: '/mydata/pdf-export',
        label: t().nav.mydataPdfExport,
        icon: DocumentTextIcon,
        feature: 'mydata',
      },
    ],
  },
  {
    to: '/reports',
    label: t().nav.reports,
    icon: ChartTrendingUpIcon,
    children: [
      {
        to: '/reports/monthly-summary',
        label: t().nav.monthlySummary,
        icon: CalendarIcon,
        feature: 'reports_monthly_summary',
      },
      {
        to: '/reports/yearly-monthly',
        label: t().nav.yearlyMonthly,
        icon: ChartBarIcon,
        feature: 'reports_yearly_monthly',
      },
      {
        to: '/reports/customer-balances',
        label: t().nav.customerBalances,
        icon: CurrencyCircleIcon,
        feature: 'reports_customer_balances',
      },
      {
        to: '/reports/aging',
        label: t().nav.aging,
        icon: ClockIcon,
        feature: 'reports_aging',
      },
      {
        to: '/reports/category-summary',
        label: t().nav.categorySummary,
        icon: ChartTrendingUpIcon,
        feature: 'reports_category_summary',
      },
      {
        to: '/reports/unallocated-payments',
        label: t().nav.unallocatedPayments,
        icon: CoinIcon,
        feature: 'reports_unallocated_payments',
      },
      {
        to: '/reports/agreement-variance',
        label: t().nav.agreementVariance,
        icon: DocumentTextIcon,
        feature: 'reports_agreement_variance',
      },
    ],
  },
  {
    to: '/settings',
    label: t().nav.settings,
    icon: CogIcon,
    feature: 'settings',
    children: [
      {
        to: '/settings/invoice-categories',
        label: t().nav.invoiceCategories,
        icon: FolderIcon,
        adminOnly: true,
        feature: 'invoice_categories',
      },
      {
        to: '/settings/expense-categories',
        label: t().nav.expenseCategories,
        icon: DocumentTextIcon,
        adminOnly: true,
        feature: 'expense_categories',
      },
      {
        to: '/settings/company-expense-categories',
        label: t().nav.companyExpenseCategories,
        icon: CurrencyCircleIcon,
        adminOnly: true,
        feature: 'company_expense_categories',
      },
      {
        to: '/settings/offices',
        label: t().nav.office,
        icon: FolderIcon,
        adminOnly: true,
        feature: 'users',
      },
      {
        to: '/settings/users',
        label: t().nav.users,
        icon: UsersIcon,
        adminOnly: true,
        feature: 'users',
      },
      {
        to: '/settings/mydata-connection',
        label: t().nav.mydataConnection,
        icon: DocumentTextIcon,
        adminOnly: true,
        feature: 'mydata',
      },
    ],
  },
];

function linkClass(collapsed: boolean) {
  return ({ isActive }: { isActive: boolean }): string =>
    [
      'group flex items-center rounded-lg text-sm transition-colors duration-150',
      collapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2',
      isActive
        ? 'bg-teal-400/15 font-medium text-white ring-1 ring-teal-300/25'
        : 'text-slate-300 hover:bg-white/10 hover:text-white',
    ].join(' ');
}

function sectionContainsPath(item: NavItem, pathname: string): boolean {
  if (item.children?.length) {
    return item.children.some((child) => sectionContainsPath(child, pathname));
  }
  if (item.to === '/') return pathname === '/';
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function readStoredOpenSections(): Set<string> {
  try {
    const raw = sessionStorage.getItem(OPEN_SECTIONS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function writeStoredOpenSections(open: Set<string>) {
  try {
    sessionStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(Array.from(open)));
  } catch {
    // ignore quota / private mode
  }
}

function NavItemContent({
  label,
  icon: Icon,
  compact,
}: {
  label: string;
  icon: IconComponent;
  compact: boolean;
}) {
  return (
    <>
      <Icon className="h-4 w-4 shrink-0 opacity-90" />
      {!compact ? <span className="truncate">{label}</span> : null}
    </>
  );
}

type SidebarProps = {
  mobileOpen: boolean;
  collapsed: boolean;
  onNavigate: () => void;
  onToggleCollapse: () => void;
};

export function Sidebar({
  mobileOpen,
  collapsed,
  onNavigate,
  onToggleCollapse,
}: SidebarProps) {
  const { isAdmin, isSuperAdmin, user } = useAuth();
  const location = useLocation();
  const labels = t().nav;
  const compact = collapsed && !mobileOpen;
  const subtitle = user?.tenantName || t().app.subtitle;
  const features = user?.features;
  const bypassFeatures = isSuperAdmin && !user?.supportLogin;
  const [openSections, setOpenSections] = useState<Set<string>>(() =>
    readStoredOpenSections(),
  );

  function allowed(item: NavItem): boolean {
    if (item.adminOnly && !isAdmin) return false;
    if (item.children && item.children.length > 0) {
      return item.children.some((child) => allowed(child));
    }
    if (!item.feature) return true;
    if (bypassFeatures) return true;
    if (hasFeature(features, item.feature)) return true;
    if (
      item.feature.startsWith('reports_') &&
      hasFeature(features, 'reports')
    ) {
      return true;
    }
    return false;
  }

  function filterNavItems(items: NavItem[]): NavItem[] {
    return items
      .map((item) => {
        if (!item.children?.length) {
          return allowed(item) ? item : null;
        }
        const children = filterNavItems(item.children);
        if (children.length === 0) return null;
        return { ...item, children };
      })
      .filter((item): item is NavItem => item != null);
  }

  const visibleNavItems = useMemo(
    () => filterNavItems(navItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- feature/role gate inputs
    [isAdmin, bypassFeatures, features],
  );

  useEffect(() => {
    const required = new Set<string>();
    const path = location.pathname;
    if (path.startsWith('/reports/shipment')) {
      required.add('/shipment-files');
    } else if (path.startsWith('/shipment-files')) {
      required.add('/shipment-files');
    } else if (path.startsWith('/reports/company-expense')) {
      required.add('/company-expenses');
    } else if (path.startsWith('/company-expenses')) {
      required.add('/company-expenses');
    } else if (path.startsWith('/reports')) {
      required.add('/reports');
    }
    if (path.startsWith('/settings')) {
      required.add('/settings');
    }
    if (required.size === 0) return;

    setOpenSections((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const key of Array.from(required)) {
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      }
      if (changed) writeStoredOpenSections(next);
      return changed ? next : prev;
    });
  }, [location.pathname]);

  function toggleSection(key: string) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeStoredOpenSections(next);
      return next;
    });
  }

  return (
    <aside
      className={[
        'fixed inset-y-0 left-0 z-40 flex h-full shrink-0 flex-col bg-sidebar text-white shadow-bf-md transition-[width,transform] duration-200 lg:static lg:translate-x-0 lg:shadow-none',
        compact ? 'w-[4.25rem]' : 'w-60',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
      ].join(' ')}
    >
      <div
        className={[
          'border-b border-white/10',
          compact ? 'px-2 py-4' : 'px-4 py-5',
        ].join(' ')}
      >
        {compact ? (
          <div
            className="mx-auto flex h-9 w-9 items-center justify-center rounded-lg bg-teal-400/15 text-sm font-semibold text-teal-200 ring-1 ring-teal-300/20"
            title={t().app.name}
          >
            BF
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-400/15 text-sm font-bold text-teal-200 ring-1 ring-teal-300/20">
              BF
            </div>
            <div className="min-w-0 pt-0.5">
              <div className="truncate font-display text-lg font-semibold tracking-tight">
                {t().app.name}
              </div>
              <div className="mt-0.5 truncate text-xs text-slate-400">
                {subtitle}
              </div>
            </div>
          </div>
        )}
      </div>

      <nav
        className={[
          'flex-1 space-y-1 overflow-y-auto scrollbar-thin',
          compact ? 'p-2' : 'p-3',
        ].join(' ')}
      >
        {visibleNavItems.map((item) => {
          const children = item.children;

          if (children && children.length > 0) {
            const SectionIcon = item.icon;
            const isOpen = openSections.has(item.to);
            const sectionActive = sectionContainsPath(item, location.pathname);

            return (
              <div key={item.to} className="pt-1">
                <button
                  type="button"
                  onClick={() => toggleSection(item.to)}
                  className={[
                    'flex w-full items-center rounded-lg text-sm transition-colors duration-150',
                    compact ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2',
                    sectionActive
                      ? 'bg-white/5 font-medium text-white'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white',
                  ].join(' ')}
                  aria-expanded={isOpen}
                  title={item.label}
                >
                  <SectionIcon className="h-4 w-4 shrink-0 opacity-90" />
                  {!compact ? (
                    <>
                      <span className="min-w-0 flex-1 truncate text-left">
                        {item.label}
                      </span>
                      <ChevronDownIcon
                        className={[
                          'h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-150',
                          isOpen ? 'rotate-0' : '-rotate-90',
                        ].join(' ')}
                      />
                    </>
                  ) : null}
                </button>

                {isOpen ? (
                  <div
                    className={[
                      'mt-0.5 space-y-0.5',
                      compact ? '' : 'pl-2',
                    ].join(' ')}
                  >
                    {children.map((child) => {
                      const end =
                        child.to === '/' ||
                        children.some(
                          (sibling) =>
                            sibling.to !== child.to &&
                            sibling.to.startsWith(`${child.to}/`),
                        );
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          end={end}
                          className={linkClass(compact)}
                          onClick={onNavigate}
                          title={child.label}
                        >
                          <NavItemContent
                            label={child.label}
                            icon={child.icon}
                            compact={compact}
                          />
                        </NavLink>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          }

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={linkClass(compact)}
              onClick={onNavigate}
              title={item.label}
            >
              <NavItemContent
                label={item.label}
                icon={item.icon}
                compact={compact}
              />
            </NavLink>
          );
        })}
      </nav>

      <div className="hidden border-t border-white/10 p-2 lg:block">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex w-full items-center justify-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
          aria-label={
            collapsed ? labels.expandSidebar : labels.collapseSidebar
          }
          title={collapsed ? labels.expandSidebar : labels.collapseSidebar}
        >
          {collapsed ? (
            <ChevronRightIcon className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeftIcon className="h-4 w-4" />
              <span>{labels.collapseSidebar}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
